import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService, LC_KEY_ACCOUNT_PRODUCT_FULL, LC_KEY_ACCOUNT_PRODUCT_FULL_HISTORY_LIST, LC_PRODUCT_FULL_STORAGE_TIME, LocalStorage } from 'src/toolkit';
import { TTableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { TProductFullList, TProductFullListResponse, TProductFullListQuery, TProductFullListSortCol, TAccountProductFullPayloadRequest, adaptAccountProductFullToRequest, adaptProductFullFromListResponse, TAccountProductFullHistoryItem } from '../../types';
import { BACKEND_ACCOUNT_PRODUCT_FULL } from '../../backend-paths';

export const accountProductFullListGetApi = createAsyncThunk(
	BACKEND_ACCOUNT_PRODUCT_FULL,
	(payload: TAccountProductFullPayloadRequest, thunkApi) => {
		const params = adaptAccountProductFullToRequest(payload);

		const key = `${LC_KEY_ACCOUNT_PRODUCT_FULL}/${JSON.stringify(params)}`;
		const value = LocalStorage.get(key);

		if (value) {
			const { updatedTimestampAt, result } = value;
			const diff = Date.now() - updatedTimestampAt;

			if (diff <= LC_PRODUCT_FULL_STORAGE_TIME) {
				console.log('FROM STORAGE');

				return result;
			}
		}

		console.log('FROM DATABASE');

		const config = {
			params,
			timeout: 60000,
		};

		return HttpService.get<TProductFullListResponse>(BACKEND_ACCOUNT_PRODUCT_FULL, config).then(response => {
			const { data } = response;

			return adaptProductFullFromListResponse(data).then(result => {
				// LocalStorage.set(key, { updatedTimestampAt: Date.now(), result });

				const historyItemList: TAccountProductFullHistoryItem[] = LocalStorage.get(LC_KEY_ACCOUNT_PRODUCT_FULL_HISTORY_LIST) || [];

				const date = new Date();
				const itemKey = JSON.stringify(payload);

				const historyItem: TAccountProductFullHistoryItem = {
					...payload,
					date: date.toISOString(),
					key: itemKey,
				};

				const filtered = historyItemList.filter(item => item.key !== itemKey);

				filtered.unshift(historyItem);

				LocalStorage.set(LC_KEY_ACCOUNT_PRODUCT_FULL_HISTORY_LIST, filtered);

				return result;
			}).catch((error: HttpError) => {
				return thunkApi.rejectWithValue({ ...error });
			});
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TProductFullList;
	showList: TProductFullList;
	query: TProductFullListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	showList: {
		items: [],
		count: 0,
	},
	query: {
		limit: 50,
		offset: 0,
		search: undefined,
		searchBrandName: undefined,
		searchSerialNumber: undefined,
		searchProvider: undefined,
		searchName: undefined,
		searchStorageName: undefined,
		brandId: undefined,
		isAnalogues: false,
		serialNumber: '',
	},
	error: undefined,
};

export const accountProductFullListSlice = createSlice({
	name: 'accountProductFullListSlice',
	initialState,
	reducers: {
		setAccountProductFullListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setAccountProductFullListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setAccountProductFullListQueryShowList(state, action: PayloadAction<TProductFullList>) {
			state.showList = action.payload;
		},

		setAccountProductFullListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setAccountProductFullListQuerySearchBrandName(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchBrandName = value;
		},

		setAccountProductFullListQuerySearchSerialNumber(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchSerialNumber = value;
		},

		setAccountProductFullListQuerySearchProvider(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchProvider = value;
		},

		setAccountProductFullListQuerySearchName(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchName = value;
		},

		setAccountProductFullListQuerySearchStorageName(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchStorageName = value;
		},

		setAccountProductFullListQueryBrand(state, action: PayloadAction<number | undefined>) {
			const brandId = action.payload || undefined;
			const isBrandId = !!brandId && brandId > 0;

			state.query.brandId = isBrandId ? brandId : undefined;
		},

		setAccountProductFullListQueryIsAnalogues(state, action: PayloadAction<boolean>) {
			state.query.isAnalogues = action.payload;
		},

		setAccountProductFullListQuerySerialNumber(state, action: PayloadAction<string>) {
			const serialNumber = action.payload || undefined;

			state.query.serialNumber = serialNumber ? serialNumber : '';
		},

		setAccountProductFullListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setAccountProductFullListQuerySortCol(state, action: PayloadAction<TProductFullListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearAccountProductFullList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[accountProductFullListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[accountProductFullListGetApi.fulfilled.type]: (state, action: PayloadAction<TProductFullList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[accountProductFullListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useAccountProductFullListSelector = () => useAppSelector(state => state[accountProductFullListSlice.name]);

export const { clearAccountProductFullList, setAccountProductFullListQuerySearch, setAccountProductFullListQuerySearchBrandName, setAccountProductFullListQuerySearchSerialNumber, setAccountProductFullListQuerySearchName, setAccountProductFullListQuerySearchStorageName, setAccountProductFullListQuerySearchProvider, setAccountProductFullListQuerySerialNumber, setAccountProductFullListQueryBrand, setAccountProductFullListQueryIsAnalogues, setAccountProductFullListQuerySort, setAccountProductFullListQuerySortCol, setAccountProductFullListQueryLimit, setAccountProductFullListQueryOffset, setAccountProductFullListQueryShowList } = accountProductFullListSlice.actions;
